import { FormEvent, useEffect, useRef, useState } from 'react';
import { useClerk, useOrganizationList } from '@clerk/react';

import { clearPendingCompanyName, readPendingCompanyName } from '@/api/tenants/linkClerkOrg';
import { Button } from '@/component-library/primitives/Button/Button';
import { Input } from '@/component-library/widgets/Input/Input';
import { AppRoutes } from '@/utils/routes/router';

type Props = {
  /** When set, pre-fills the name field (Path B registration company name). */
  initialName?: string | null;
};

/**
 * Org create step with optional logo — used instead of Clerk's TaskChooseOrganization
 * when we already have a company name from /register-company.
 *
 * Uses `clerk.createOrganization` (not only useOrganizationList) because during the
 * choose-organization session task, list hooks may be unavailable while isSignedIn is false.
 */
export default function PrefillCreateOrganization({ initialName }: Props) {
  const clerk = useClerk();
  const { isLoaded, createOrganization, setActive } = useOrganizationList();
  const [name, setName] = useState(() => (initialName ?? readPendingCompanyName() ?? '').trim());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoFileRef = useRef<File | null>(null);

  useEffect(() => {
    const next = initialName?.trim();
    if (next) {
      setName(next);
    }
  }, [initialName]);

  const onLogoChange = (file: File | null) => {
    logoFileRef.current = file;
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(file ? URL.createObjectURL(file) : null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Company name is required.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      console.info('[ChurnSignal][PathB-Org] PrefillCreateOrganization submit', {
        name: trimmed,
        hasCreateOrganizationHook: Boolean(createOrganization),
        hasClerkCreateOrganization: typeof clerk.createOrganization === 'function',
        clerkLoaded: clerk.loaded,
      });

      const org =
        createOrganization && isLoaded
          ? await createOrganization({ name: trimmed })
          : await clerk.createOrganization({ name: trimmed });

      const logo = logoFileRef.current;
      if (logo) {
        try {
          await org.setLogo({ file: logo });
        } catch (logoError: unknown) {
          console.warn('[ChurnSignal] Org created but logo upload failed:', logoError);
        }
      }

      if (setActive && isLoaded) {
        await setActive({ organization: org.id });
      } else {
        await clerk.setActive({ organization: org.id });
      }

      clearPendingCompanyName();
      window.location.assign(AppRoutes.dashboard);
    } catch (err: unknown) {
      console.warn('[ChurnSignal][PathB-Org] PrefillCreateOrganization failed', err);
      const message = err instanceof Error ? err.message : 'Failed to create organization.';
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="w-full max-w-md space-y-5">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-custom-black">Confirm your organization</h2>
        <p className="mt-1 text-sm text-custom-darkSilver">
          We prefilled the company name from registration. You can edit it and optionally add a logo.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50">
          {logoPreview ? (
            <img src={logoPreview} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs text-custom-darkSilver">Logo</span>
          )}
        </div>
        <label className="cursor-pointer text-sm font-medium text-custom-blue hover:underline">
          {logoPreview ? 'Change logo' : 'Upload logo (optional)'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(ev) => onLogoChange(ev.target.files?.[0] ?? null)}
          />
        </label>
      </div>

      <Input
        label="Company name"
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        placeholder="Company name"
      />

      {error && <p className="text-sm text-custom-red">{error}</p>}

      <Button type="submit" className="w-full" disabled={busy || !clerk.loaded}>
        {busy ? 'Creating…' : 'Continue'}
      </Button>
    </form>
  );
}
